import axios from 'axios';
import store from '@/store';
import FileFactory from '@/factories/FileFactory';
import File from '@/model/File';
import Image from '@/model/Image';
import Directory from '@/model/Directory';
import {TestFileSystem} from '@/tests/TestFileSystem';

class ApiConnector
{
	/**
	 * Load the whole file system - including all files and directories - from the server
	 */
	public async loadFileSystem()
	{
		if (process.env.NODE_ENV === 'development')
		{
			return TestFileSystem;
		}

		try
		{
			const response = await axios.get(store.state.apiBaseUrl + '/load-file-system');

			if (response.data.fileSystem)
			{
				return JSON.parse(response.data.fileSystem);
			}
		}
		catch (e)
		{
			//todo implement error handling
		}
	}

	public async moveDirectory(directory: Directory, to: Directory)
	{
		//file system object models can just be moved between directories!
		if (!(to instanceof Directory))
		{
			return false;
		}

		try
		{
			const response = await axios.post(store.state.apiBaseUrl + '/move-directory', {id: directory.id, toDirectoryId: to.id});

			return response.data.success;
		}
		catch (e)
		{
			//todo: implement error handling
		}
	}

	public async moveFile(file: File, to: Directory)
	{
		//file system object models can just be moved between directories!
		if (!(to instanceof Directory))
		{
			return false;
		}

		try
		{
			const response = await axios.post(store.state.apiBaseUrl + '/move-file', {id: file.id, toDirectoryId: to.id});

			return response.data.success;
		}
		catch (e)
		{
			//todo: handle error message
		}
	}

	public async renameDirectory(directory: Directory, newName: string)
	{
		try
		{
			const response = await axios.post(store.state.apiBaseUrl + '/rename-directory', {id: directory.id, newName});

			return response.data.success;
		}
		catch (e)
		{
			return false;
		}
	}

	public async renameFile(file: File, newName: string)
	{
		try
		{
			const response = await axios.post(store.state.apiBaseUrl + '/rename-file', {id: file.id, newName});

			return response.data.success;
		}
		catch (e)
		{
			return false;
		}
	}

	public async getImageDimensions(image: Image)
	{
		try
		{
			const response = await axios.get(store.state.apiBaseUrl + '/get-image-dimensions/' + image.id);

			if (response.data.success)
			{
				return {
					width: response.data.width,
					height: response.data.height
				};
			}
		}
		catch (e)
		{
			return false;
		}
	}

	public async resizeImage(image: Image, width: number, height: number)
	{
		try
		{
			const response = await axios.post(store.state.apiBaseUrl + '/resize-image/' + image.id, {width, height});

			return response.data.success;
		}
		catch (e)
		{
			return false;
		}
	}

	public async createDirectory(newDirectory: Directory)
	{
		try
		{
			const response = await axios.post(store.state.apiBaseUrl + '/create-directory', {name: newDirectory.name, parentId: newDirectory.parent.id});

			if (response.data.success && response.data.createdDirectory)
			{
				const createdDirectory = response.data.createdDirectory;

				newDirectory.id = createdDirectory.id;

				return true;
			}

			return false;
		}
		catch (e)
		{
			//todo: implement error handling
		}
	}

	public async downloadFile(file: File)
	{
		//we need the real name in order to know the extension of the created file
		if (!file.realName)
		{
			return false;
		}

		try
		{
			const response = await axios.get(store.state.apiBaseUrl + '/download-file/' + file.id, {
				responseType: 'blob'
			});

			if (response.data)
			{
				const blob = new Blob([response.data], {type: file.mimeType});
				const link = document.createElement('a');
				const objectUrl = URL.createObjectURL(blob);

				link.href = objectUrl;
				link.download = file.realName;
				link.click();
				URL.revokeObjectURL(objectUrl);
			}
		}
		catch (e)
		{
			//todo: implement error handling
		}
	}

	public async downloadDirectory(directory: Directory)
	{
		try
		{
			const response = await axios.get(store.state.apiBaseUrl + '/download-directory/' + directory.id, {
				responseType: 'blob'
			});

			if (response.data)
			{
				const blob = new Blob([response.data], {type: 'application/zip'});
				const link = document.createElement('a');
				const objectUrl = URL.createObjectURL(blob);

				link.href = objectUrl;
				link.download = directory.name + '.zip';
				link.click();
				URL.revokeObjectURL(objectUrl);
			}
		}
		catch (e)
		{
			//todo: implement error handling
		}
	}

	public async copyDirectory(directory: Directory)
	{
		try
		{
			const response = await axios.post(store.state.apiBaseUrl + '/copy-directory/' + directory.id);

			if (response.data.success)
			{
				const newDirectory = new Directory(directory);
				newDirectory.id = response.data.newId;

				return newDirectory;
			}

			return null;
		}
		catch (e)
		{
			//todo: implement error handling
		}
	}

	public async copyFile(file: File)
	{
		try
		{
			const response = await axios.post(store.state.apiBaseUrl + '/copy-file/' + file.id);

			if (response.data.success)
			{
				return FileFactory.create(file.directory, JSON.parse(response.data.newFile));
			}

			return null;
		}
		catch (e)
		{
			//todo: implement error handling
		}
	}

	public async deleteDirectory(directory: Directory)
	{
		try
		{
			const response = await axios.post(store.state.apiBaseUrl + '/delete-directory', {id: directory.id});

			return response.data.success;
		}
		catch (e)
		{
			return false;
		}
	}

	public async deleteFile(file: File)
	{
		try
		{
			const response = await axios.post(store.state.apiBaseUrl + '/delete-file', {id: file.id});

			return response.data;
		}
		catch (e)
		{
			return false;
		}
	}

	public async upload(directory: Directory, files: SystemFile[], progressCallback: any)
	{
		const formData = new FormData();

		if (!directory || !(directory instanceof Directory))
		{
			return false;
		}

		formData.append('directoryId', directory.id.toString());

		let i = 1;
		for (const file of files)
		{
			formData.append('files' + i, file);
			i++;
		}

		try
		{
			const response = await axios.post(store.state.apiBaseUrl + '/upload', formData, {
				onUploadProgress: progressCallback
			});

			if (response.data.success)
			{
				const uploadedFiles = response.data.uploadedFiles;

				for (const file of uploadedFiles)
				{
					const uploadedFile = FileFactory.create(directory, file);

					directory.files.push(uploadedFile);
				}

				return true;
			}

			return false;
		}
		catch (e)
		{
			return false;
		}
	}
}

export default ApiConnector;